// Cross-task dependency primitives. DFS cycle detection per eng review §1.
// Lane A scaffold; UI wiring lands in Lane B.

export interface TaskGraphNode {
  id: string;
  blockedBy?: string[];
  status: string;
}

export class DependencyCycleDetected extends Error {
  constructor(public path: string[]) {
    super(`依赖关系成环: ${path.join(' → ')}`);
    this.name = 'DependencyCycleDetected';
  }
}

/**
 * Returns the cycle path if adding `newBlockedBy` to `taskId` would create a
 * cycle, or null if safe to commit.
 *
 * Standard DFS with WHITE/GRAY/BLACK coloring. The graph is small (≤600
 * tasks at VAVA scale), so we don't optimize.
 */
export function detectCycle(
  taskId: string,
  newBlockedBy: string[],
  allTasks: TaskGraphNode[]
): string[] | null {
  const byId = new Map<string, TaskGraphNode>();
  for (const t of allTasks) byId.set(t.id, t);

  // Build a virtual node for the proposed edit so we don't mutate input.
  const virtualMe: TaskGraphNode = {
    id: taskId,
    status: 'virtual',
    blockedBy: newBlockedBy
  };
  byId.set(taskId, virtualMe);

  const WHITE = 0, GRAY = 1, BLACK = 2;
  const color = new Map<string, number>();
  const parent = new Map<string, string>();

  function dfs(id: string): string[] | null {
    color.set(id, GRAY);
    const node = byId.get(id);
    if (node && node.blockedBy) {
      for (const depId of node.blockedBy) {
        const c = color.get(depId) ?? WHITE;
        if (c === GRAY) {
          // Back-edge — reconstruct the cycle path.
          const cycle: string[] = [depId, id];
          let cur = id;
          while (parent.has(cur) && cur !== depId) {
            cur = parent.get(cur)!;
            cycle.push(cur);
          }
          return cycle.reverse();
        }
        if (c === WHITE) {
          parent.set(depId, id);
          const found = dfs(depId);
          if (found) return found;
        }
      }
    }
    color.set(id, BLACK);
    return null;
  }

  return dfs(taskId);
}

/**
 * Is this task blocked by any unfinished dependency?
 * Used for the grayed-card UI hint.
 */
export function isBlocked(task: TaskGraphNode, allTasks: TaskGraphNode[]): boolean {
  if (!task.blockedBy || task.blockedBy.length === 0) return false;
  const byId = new Map<string, TaskGraphNode>();
  for (const t of allTasks) byId.set(t.id, t);
  return task.blockedBy.some(id => {
    const dep = byId.get(id);
    return dep && dep.status !== '已完成' && dep.status !== '已取消';
  });
}
